import isEqual from 'lodash.isequal';
import { FormField } from '../types/form';
import { FormOptions, SurveyProps } from '../types/survey';

export const FORM_CONFIG_DOC_ID = 'form';

type SurveyFormConfigDoc = {
  formFields?: FormField[];
  formOptions?: FormOptions;
};

type ResolveSurveyFormConfigArgs = {
  surveyDocData?: Partial<SurveyProps> | null;
  formConfigData?: SurveyFormConfigDoc | null;
  context?: string;
  surveyId?: string;
};

type ResolveSurveyFormConfigResult = {
  formFields: FormField[];
  formOptions?: FormOptions;
  source: 'config' | 'survey' | 'empty';
};

function isValidFormFields(formFields: unknown): formFields is FormField[] {
  return Array.isArray(formFields);
}

function isValidFormOptions(formOptions: unknown): formOptions is FormOptions {
  return !!formOptions && typeof formOptions === 'object';
}

export function resolveSurveyFormConfig({
  surveyDocData,
  formConfigData,
}: ResolveSurveyFormConfigArgs): ResolveSurveyFormConfigResult {
  const surveyFields = surveyDocData?.formFields;
  const surveyOptions = surveyDocData?.formOptions;
  const configFields = formConfigData?.formFields;
  const configOptions = formConfigData?.formOptions;

  const configValid =
    isValidFormFields(configFields) && isValidFormOptions(configOptions);
  const surveyValid =
    isValidFormFields(surveyFields) && isValidFormOptions(surveyOptions);
  const configMismatch =
    configValid &&
    surveyValid &&
    (!isEqual(configFields, surveyFields) ||
      !isEqual(configOptions, surveyOptions));

  if (configMismatch) {
    return {
      formFields: surveyFields,
      formOptions: surveyOptions,
      source: 'survey',
    };
  }

  if (configValid) {
    return {
      formFields: configFields,
      formOptions: configOptions,
      source: 'config',
    };
  }

  if (surveyValid) {
    return {
      formFields: surveyFields,
      formOptions: surveyOptions,
      source: 'survey',
    };
  }

  return {
    formFields: isValidFormFields(surveyFields) ? surveyFields : [],
    formOptions: isValidFormOptions(surveyOptions) ? surveyOptions : undefined,
    source: 'empty',
  };
}
